import { useEffect, useState } from 'react';
import { useTaskStore } from '@/stores/taskStore';
import type { Task } from '@/types';

export interface TitleFieldProps {
  task: Task;
}

export function TitleField({ task }: TitleFieldProps) {
  const [local, setLocal] = useState(task.title);
  const updateTask = useTaskStore((s) => s.updateTask);

  useEffect(() => {
    // Sync local state when the active task switches (task.id changes).
    // This is intentional prop-to-state sync for a controlled optimistic input.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLocal(task.title);
  }, [task.id, task.title]);

  useEffect(() => {
    if (local === task.title) return;
    const id = setTimeout(() => updateTask(task.id, { title: local }), 500);
    return () => clearTimeout(id);
  }, [local, task.id, task.title, updateTask]);

  return (
    <input
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => {
        if (local !== task.title) updateTask(task.id, { title: local });
      }}
      placeholder="Untitled task"
      aria-label="Task title"
      // Names aren't prose — red squiggles under a task title read as errors.
      spellCheck={false}
      className="w-full rounded-md bg-transparent px-1 text-base font-medium tracking-tight text-t1 outline-none placeholder:text-t3 focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset"
    />
  );
}
